import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import React, { Component } from 'react';
import { PrintOnOptions } from '../../data/printOn';
import Grid from '../../grid';
import Modal from '../../modal';
import UI from '../../ui';
import Utils from '../../utils';
import Navigation from '../../utils/Navigation';
import Util from '../Util';

const { ModalContext } = Modal;
const { GridPanel } = Grid;
const { WrappedButton, WrappedInputText, InlineMessage, WrappedButtonType } =
	UI;
const { Logger, Session } = Utils;

const columnDefs = [
	{
		field: 'media',
		filter: true,
		flex: 1,
		checkboxSelection: true,
	},
];

class PrintOnOptionsView extends Component {
	constructor(props) {
		super(props);

		const rowData = this.buildRowData();

		this.state = {
			rowData,
		};
	}

	buildPreferredPrintMedia = () => {
		const preferredPrintMedia = [];
		const logPrintMedia = [];

		/**
		 * The following uses ag-grid `forEachLeafNode` method to iterates through each node (ignoring any filtering or sorting applied within the grid)
		 * and convert it into `PreferredPrintMedia` format
		 */
		if (this.gridApi && isFunction(this.gridApi.forEachLeafNode)) {
			this.gridApi.forEachLeafNode(({ data = {}, selected }) => {
				preferredPrintMedia.push({
					value: data.value,
					userPref: selected,
				});

				logPrintMedia.push(
					selected ? `${data.value}=true` : `${data.value}=false`
				);
			});

			Logger.logToServer({
				category: 'toggle_print_media',
				printMedia: logPrintMedia,
			});
		}

		return preferredPrintMedia;
	};

	buildPreferredPrintOptions = () => {
		const { PreferredPrintMedia } = Session.getPreferences();
		const preferredPrintMedia =
			PreferredPrintMedia === '' || PreferredPrintMedia === 'newuser' // default value for new users is `newuser` per Genaro
				? []
				: JSON.parse(PreferredPrintMedia);

		return isArray(preferredPrintMedia)
			? preferredPrintMedia.reduce((result, preference) => {
					const { value } = preference;
					result[value] = preference.userPref;
					return result;
				}, {})
			: {};
	};

	buildRowData = () => {
		const preferredPrintOptions = this.buildPreferredPrintOptions();
		const rowData = [];
		const {
			planDetails: { isPayAsYouGo },
		} = Session.getAccountInfo();

		forEach(PrintOnOptions, ({ label, value }) => {
			rowData.push({
				media: label,
				value,
				checked: preferredPrintOptions.hasOwnProperty(value)
					? preferredPrintOptions[value]
					: true,
			});
		});

		// Filter out Certified Mail options for PAYG users
		if (isPayAsYouGo) {
			return rowData.filter(
				row => !row.media.toLowerCase().includes('certified')
			);
		}

		return rowData;
	};

	onClickDone = async () => {
		const { completionCallback } = this.props;

		if (isFunction(completionCallback)) {
			completionCallback({ close: true });
		}
	};

	onGridReady = ({ api }) => {
		const clearSelection = false;
		const suppressFinishEvent = true;

		this.gridApi = api;

		// check if the node should be checked
		this.gridApi.forEachLeafNode(node => {
			const { data } = node;

			node.setSelected(
				data.checked === true,
				clearSelection,
				suppressFinishEvent
			);
		});
	};

	onChangeSearch = ({ target: { value } }) => {
		this.gridApi.setQuickFilter(value);
	};

	onSelectionChanged = () => {
		const { showError } = this.context;
		const preferredPrintMedia = this.buildPreferredPrintMedia();

		if (preferredPrintMedia.length > 0) {
			Util.saveNewPreferences(
				{ preferredPrintMedia: JSON.stringify(preferredPrintMedia) },
				showError
			);
		}
	};

	upgradeButton = () => {
		return (
			<WrappedButton
				buttonType={WrappedButtonType.Primary}
				label="Upgrade"
				onClick={() => {
					Navigation.navigateTo('storeChangePlan');
				}}
			/>
		);
	};

	render() {
		const { rowData } = this.state;
		const {
			planDetails: { isPayAsYouGo },
		} = Session.getAccountInfo();

		return (
			<div className="settings-mail-print-on-options-view">
				<div className="settings-body">
					<div className="settings-content-header">
						<p>Print On Options</p>
						<hr />
					</div>
					<div className="p-grid p-dir-col">
						<p className="p-col-12">
							Select which options should be shown in the Print On list.
						</p>
						{isPayAsYouGo && (
							<div className="p-col-12">
								<InlineMessage
									type="upgrade"
									text="Certified Mail is available for paid subscriptions only. Upgrade your plan to send Certified Mail."
									actions={this.upgradeButton()}
								/>
							</div>
						)}
						<div className="settings-search p-col-12">
							<WrappedInputText
								className="search-input"
								placeholder="Search Print On"
								name="searchbar"
								onChange={this.onChangeSearch}
								dataqa="settingsPrintOnOptions-searchPrintOn"
							/>
							<i
								className="pi pi-search search-icon"
								aria-label="Search"
								data-qa="settingsPrintOnOptions-searchPrintIcon"
								{...{ dataqa: 'settingsPrintOnOptions-searchPrintIcon' }}
							/>
						</div>
						<div className="settings-list p-col-12">
							<GridPanel
								headerHeight={0}
								rowData={rowData}
								columnDefs={columnDefs}
								showPagination={false}
								rowMultiSelectWithClick
								onGridReady={this.onGridReady}
								onSelectionChanged={this.onSelectionChanged}
								dataqa="print-on-options-grid"
							/>
						</div>
					</div>
				</div>
				<div className="settings-content-footer">
					<WrappedButton
						className="settings-button"
						label="Save"
						type="button"
						onClick={this.onClickDone}
						dataqa="print-on-options-save"
					/>
				</div>
			</div>
		);
	}
}

PrintOnOptionsView.contextType = ModalContext;

export default PrintOnOptionsView;
